import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../../components/AppShell";
import T from "../../components/T";
import { ChevronLeft, Check } from "lucide-react";
import { FINANCIAL_CHECKLIST } from "../../data/v11Static";
import { getFinChecklist, setFinItem } from "../../lib/localProgress";
import { lightTap, successPulse } from "../../lib/haptics";

export default function FinancialFreedom() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [state, setState] = useState({});

  useEffect(() => { setState(getFinChecklist()); }, []);

  const toggle = (id) => {
    const next = !state[id];
    setFinItem(id, next);
    const nextState = { ...state, [id]: next };
    setState(nextState);
    if (next) {
      // Stronger pulse only when the user completes the LAST remaining item.
      const allDone = FINANCIAL_CHECKLIST.every((it) => nextState[it.id]);
      if (allDone) successPulse(); else lightTap();
    }
  };

  const total = FINANCIAL_CHECKLIST.length;
  const done = FINANCIAL_CHECKLIST.filter((it) => state[it.id]).length;
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  let scoreCopy;
  if (done === 0) scoreCopy = t("v11.financial.score_zero");
  else if (done < 4) scoreCopy = t("v11.financial.score_low");
  else if (done < 8) scoreCopy = t("v11.financial.score_mid");
  else scoreCopy = t("v11.financial.score_high");

  return (
    <AppShell>
      <div className="px-8 pt-16 pb-6" data-testid="page-financial-readiness">
        <button onClick={() => navigate("/sanctuary")} className="flex items-center gap-1 text-sm text-foreground/55 hover:text-brand mb-2">
          <ChevronLeft className="w-4 h-4" /> {t("v11.back_to_sanctuary")}
        </button>
        <p className="font-script text-2xl text-brand">{t("v11.financial.kicker")}</p>
        <h1 className="font-serif text-4xl leading-tight"><T i18nKey="v11.financial.h" /></h1>
        <p className="mt-3 text-foreground/70 leading-relaxed">{t("v11.financial.intro")}</p>
      </div>

      <div className="px-8 mb-5">
        <div className="rounded-3xl bg-brand/8 border border-brand/20 p-5" data-testid="fin-score">
          <p className="text-xs uppercase tracking-widest text-brand-deep">{t("v11.financial.score_label")}</p>
          <p className="mt-1 font-serif text-2xl">{t("v11.financial.items_complete", { n: done, total })}</p>
          <div className="mt-3 h-2 rounded-full bg-foreground/10 overflow-hidden">
            <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-3 font-serif italic text-foreground/85">{scoreCopy}</p>
        </div>
      </div>

      <div className="px-8 pb-10 space-y-2.5">
        {FINANCIAL_CHECKLIST.map((it) => {
          const checked = !!state[it.id];
          return (
            <button
              key={it.id}
              data-testid={`fin-item-${it.id}`}
              onClick={() => toggle(it.id)}
              className={`w-full text-left rounded-3xl p-4 border transition-all flex items-start gap-3 ${
                checked
                  ? "bg-brand/10 border-brand/40"
                  : "bg-card border-border hover:border-brand/40"
              }`}
            >
              <span className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center shrink-0 border ${
                checked ? "bg-brand border-brand text-white" : "border-foreground/30"
              }`}>
                {checked && <Check className="w-3.5 h-3.5" />}
              </span>
              <div>
                <div className={`font-medium ${checked ? "line-through text-foreground/60" : ""}`}>
                  {lang === "es" ? it.title_es : it.title_en}
                </div>
                <p className="text-xs text-foreground/60 mt-1 leading-relaxed">
                  {lang === "es" ? it.note_es : it.note_en}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </AppShell>
  );
}
